<template>
  <div>
      <div class="p-4 card">
        <div>
          <b><u>Attainment Calculation Method</u></b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;" class="fas fa-edit"
            @click="toggleEdit"></i>
          <br />
          There are two methods for calculating CO attainment of a Course. Choose one of the methods listed below:
        </div>

        <b-row>
          <!-- Method 1 Info -->
          <b-form-group class="col">
            <i style="font-size: 16px !important; color: #1ea2ed;" @click="$bvModal.show('showMethod1');"
              class="fas fa-info-circle fa-xm"></i>
            <b-modal class="no-print" title="Method 1: Question-Based Targeted Attainment Method (QBTAM)"
              id="showMethod1" size="md" hide-footer centered>
              <p><strong>Explanation:</strong> This method calculates the attainment level for each CO based on the
                percentage of students who achieved the target percentage for individual questions mapped to that CO.
              </p>
              <h5><b><u>Steps:</u></b></h5>
              <p>
                1. The CO mapped question-wise marks of each student for an assessment are noted.
              </p>
              <p>
                2. The marks obtained for each question are converted to a percentage score for that question.
              </p>
              <p>
                3. This percentage marks for each question are then compared with the target percentage of the CO to
                which
                the question is mapped.
              </p>
              <p>
                4. The total number of students who have achieved the CO target for each question is obtained.
              </p>
              <p>
                5. This total number of students who achieved the target is converted to a percentage value.
              </p>
              <p>
                6. This percentage is compared to a threshold value, and the attainment level for each question is
                determined.
              </p>
              <p>
                7. The average value of the attainment level of all questions mapped to the same CO is calculated.
              </p>
              <p>
                8. This average value becomes the attainment value for each CO for that assessment.
              </p>

              <h5><b><u>Advantages:</u></b></h5>
              <p>
                1. Granular Analysis: By assessing attainment at the question level, educators gain detailed insights
                into
                student performance, allowing for targeted interventions.
              </p>
              <p>
                2. Sensitivity to Question Difficulty: This method considers the difficulty level of individual
                questions,
                providing a more nuanced understanding of CO attainment.
              </p>
              <p>
                3. Flexibility: Educators can adjust the target percentage for each CO based on the difficulty level
                of
                questions, allowing for more accurate assessment.
              </p>

              <h5><b><u>Disadvantages:</u></b></h5>
              <p>
                1. Complexity: The method involves analyzing performance at the question level, which may be more
                complex
                compared to Method 2.
              </p>
              <p>
                2. Subjectivity in Setting Target Percentage: Setting target percentages for individual questions
                requires
                careful consideration and may introduce subjectivity into the assessment process.
              </p>
              <b-button variant="danger" size="sm" @click="$bvModal.hide('showMethod1')">Close</b-button>
            </b-modal>
            <label><b>Method 1:</b> Question-Based Targeted Attainment Method (QBTAM)</label>
          </b-form-group>

          <!-- Method 2 Info -->
          <b-form-group class="col">
            <i v-if="!usersubgroup_id" style="font-size: 16px !important; color: #1ea2ed;" @click="$bvModal.show('showMethod2');"
              class="fas fa-info-circle fa-xm"></i>
            <b-modal class="no-print" title="Method 2: CO-Targeted Average Attainment Method (CTAAM)" id="showMethod2"
              size="md" hide-footer centered>
              <p><strong>Explanation:</strong> This method calculates the attainment level for each CO by averaging the
                percentage marks achieved by students against the target percentage for each CO.</p>
              <h5><b><u>Steps:</u></b></h5>
              <p>
                1. The CO mapped question-wise marks of each student for an assessment are noted.
              </p>
              <p>
                2. The average marks for each CO are calculated from these marks.
              </p>
              <p>
                3. The percentage marks for each CO are calculated based on the average marks.
              </p>
              <p>
                4. The percentage marks for each CO are then compared with the target percentage for each CO.
              </p>
              <p>
                5. The total number of students who have achieved the target for each CO is obtained.
              </p>
              <p>
                6. From this, the percentage of students who achieved the target for each CO is calculated.
              </p>
              <p>
                7. This percentage is compared to a threshold value, and the attainment level for each CO is
                determined.
              </p>
              <h5><b><u>Advantages:</u></b></h5>
              <p>
                1. Simplicity: The method is straightforward and easy to understand, making it accessible to
                educators.
              </p>
              <p>
                2. Focus on Overall CO Performance: By averaging the performance of students across all questions
                mapped
                to a particular CO, this method provides a holistic view of CO attainment.
              </p>
              <p>
                3. Clear Comparison to Target: By comparing the average percentage marks for each CO with the target
                percentage, educators can easily assess whether the COs are being met.
              </p>
              <h5><b><u>Disadvantages:</u></b></h5>
              <p>
                1. Potential Oversimplification: Averaging the marks may oversimplify the assessment, potentially
                masking
                variations in student performance across different questions.
              </p>
              <p>
                2. Lack of Granularity: This method does not provide detailed insights into student performance at the
                question level, which may limit the ability to identify specific areas for improvement.
              </p>
              <p>
                3. Ignoring Question Difficulty: It does not take into account the difficulty level of individual
                questions, which may impact the overall CO attainment assessment.
              </p>
              <b-button variant="danger" size="sm" @click="$bvModal.hide('showMethod2')">Close</b-button>
            </b-modal>
            <label><b>Method 2:</b> CO-Targeted Average Attainment Method (CTAAM)</label>
          </b-form-group>
        </b-row>

        <!-- Selection Table -->
        <b-row>
          <b-col md="12">
            <div v-if="config">
              <table class="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Assessment</th>
                    <th>Board Exam</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b-form-group label="Choose Equation Type:">
                        <b-form-radio v-model="selectedEquationType.assignment" :value="1" :disabled="!isEditing">Method
                          1</b-form-radio>
                        <b-form-radio v-model="selectedEquationType.assignment" :value="2" :disabled="!isEditing">Method
                          2</b-form-radio>
                      </b-form-group>
                    </td>
                    <td>
                      <b-form-group label="Choose Equation Type:">
                        <b-form-radio v-model="selectedEquationType.assessment" :value="1" :disabled="!isEditing">Method
                          1</b-form-radio>
                        <b-form-radio v-model="selectedEquationType.assessment" :value="2" :disabled="!isEditing">Method
                          2</b-form-radio>
                      </b-form-group>
                    </td>
                    <td>
                      <b-form-group label="Choose Equation Type:">
                        <b-form-radio v-model="selectedEquationType.boardexam" :value="1" :disabled="!isEditing">Method
                          1</b-form-radio>
                        <b-form-radio v-model="selectedEquationType.boardexam" :value="2" :disabled="!isEditing">Method
                          2</b-form-radio>
                      </b-form-group>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
        </b-row>

        <!-- Action Buttons -->
        <div>
          <b-button variant="danger" class="m-2" @click="clearSelectedMethods()" size="sm" :disabled="!isEditing">Clear all</b-button>
          <b-button variant="info" class="m-2" @click="setAttainmentConfiguration()"
            :disabled="!isEditing">Update</b-button>
        </div>
      </div>
      <br><br>
      <div class="p-4 card">
        <div>
          <b><u>Attainment Threshold</u></b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;" class="fas fa-edit"
            @click="toggleThresholdEdit"></i>
          <br />
          Set the threshold values to be applied for all direct assessment tools for calculating attainment level:
        </div>

        <div class="mb-2 mt-2" v-if="!usersubgroup_id">
          <b-form-checkbox
            v-model="attainmentThresholds.locked"
            :disabled="!isThresholdEditing"
          >Lock this threshold values for attainment report generation</b-form-checkbox>
        </div>

        <div class="mb-4">
          <b-form-checkbox
            v-model="attainmentThresholds.equal_thresholds"
            :disabled="!isThresholdEditing"
            @change="changeInAttainmentToolsEqualThresholds"
          >Apply same threshold for all direct assessment tools (Assignment, Assessment, Laboratory, Project, Seminar, University Exam)</b-form-checkbox>
        </div>

        <div v-if="attainmentThresholds.equal_thresholds">
          <ul>
            <li v-for="(attainment, attainmentIndex) in attainmentThresholds.assessment" :key="attainmentIndex">
              <b-row class="mx-2 my-1">
                <span class="mr-1" style="align-self: center;">If</span>
                <b-form-select class="mr-1" style="width: 60px;" :options="operators"
                  v-model="attainment.comparisan" :disabled="!isThresholdEditing"></b-form-select>
                <b-form-input class="mr-1" style="width: 60px;"
                  v-model="attainment.compare_by" :disabled="!isThresholdEditing"></b-form-input>
                <span class="mr-1" style="align-self: center;">% of students achieved the target, then the attainment level is</span>
                <b-form-input class="mr-4" style="width: 60px;" v-model.number="attainment.value"
                  :disabled="!isThresholdEditing"></b-form-input>
                <span v-if="isThresholdEditing" style="align-self: center;" class="btn p-0 m-0 text-danger"
                  @click="attainmentThresholds.assessment.splice(attainmentIndex, 1)">
                  <i class="fas fa-minus-circle" style="font-size:14px !important;"></i>
                </span>
              </b-row>
            </li>
            <li v-if="isThresholdEditing">
              <b-row class="mx-2 my-1">
                <b-link @click="addThresholdValue('assessment')">Add Level</b-link>
              </b-row>
            </li>
          </ul>
        </div>

        <div v-else>
          <div v-for="tool in ['assignment', 'assessment', 'laboratory', 'project', 'seminar', 'boardexam']" :key="tool">
            <h5 style="font-weight: bold;">{{ tool.charAt(0).toUpperCase() + tool.slice(1) === 'Boardexam' ? 'University Exam' : tool.charAt(0).toUpperCase() + tool.slice(1) }}</h5>
            <ul>
              <li v-for="(attainment, attainmentIndex) in attainmentThresholds[tool]" :key="attainmentIndex">
                <b-row class="mx-2 my-1">
                  <span class="mr-1" style="align-self: center;">If</span>
                  <b-form-select class="mr-1" style="width: 60px;" :options="operators"
                    v-model="attainment.comparisan" :disabled="!isThresholdEditing"></b-form-select>
                  <b-form-input class="mr-1" style="width: 60px;"
                    v-model="attainment.compare_by" :disabled="!isThresholdEditing"></b-form-input>
                  <span class="mr-1" style="align-self: center;">% of students achieved the target, then the attainment level is</span>
                  <b-form-input class="mr-4" style="width: 60px;" v-model.number="attainment.value"
                    :disabled="!isThresholdEditing"></b-form-input>
                  <span v-if="isThresholdEditing" style="align-self: center;" class="btn p-0 m-0 text-danger"
                    @click="attainmentThresholds[tool].splice(attainmentIndex, 1)">
                    <i class="fas fa-minus-circle" style="font-size:14px !important;"></i>
                  </span>
                </b-row>
              </li>
              <li v-if="isThresholdEditing">
                <b-row class="mx-2 my-1">
                  <b-link @click="addThresholdValue(tool)">Add Level</b-link>
                </b-row>
              </li>
            </ul>
          </div>
        </div>

        <div>
          <b-button variant="info" class="m-2" @click="setAttainmentThreshold()"
          :disabled="!isThresholdEditing">Update</b-button>
        </div>
      </div>

      <br><br>
      <div class="p-4 card">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <b><u>Weightage</u></b>          
        </div>
        <b-form-checkbox v-if="!usersubgroup_id" v-model="weightageLocked" @change="setWeightageLocked" class="mb-0">
          Lock this weightage values for attainment report generation
        </b-form-checkbox>
        <hr>

        <!-- Section 1: CIE Pairs -->
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <b>Percentage (CIE)</b>
            <span class="ml-1 text-muted" style="font-size: 13px;">Weightage pairs for continuous internal evaluation</span>
          </div>
          <div v-if="!usersubgroup_id">
            <b-button variant="info" size="sm" class="m-2" @click="openAddPairModal()">
              <i class="fas fa-plus" style="font-size: 10px !important;"></i> Add New Pair
            </b-button>
          </div>
        </div>
        <div class="mt-2">
          <div v-for="(pair, pairIndex) in weightagePairs" :key="pairIndex"
            class="d-flex align-items-center justify-content-between py-1 px-2 mb-1 border rounded">
            <span>
              <strong>{{ pairIndex + 1 }}.</strong>
              <span class="ml-2 text-muted" style="font-size: 12px;">
                <template v-for="type in weightageTypes">
                  <span v-if="pair.types[type.key] && pair.types[type.key].selected" :key="type.key" class="mr-2">
                    {{ type.label }}: {{ pair.types[type.key].weightage }}%
                  </span>
                </template>
              </span>
            </span>
            <span v-if="!usersubgroup_id" class="btn p-0 m-0 text-danger" @click="removeWeightagePair(pairIndex)">
              <i class="fas fa-minus-circle" style="font-size:14px !important;"></i>
            </span>
          </div>          
        </div>

        <hr>

        <!-- Section 2: Direct Attainment Split -->
        <div class="d-flex align-items-center">
          <b>Percentage Weightage Split for Direct Attainment Calculation</b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;"
            class="fas fa-edit" @click="toggleDirectSplitEdit"></i>
        </div>
        <div class="mt-2">
          <table class="table table-bordered table-sm" style="max-width: 400px;">
            <tbody>
              <tr>
                <td>Continuous Internal Evaluation (CIE)</td>
                <td>
                  <b-form-input type="number" style="width: 100px;" v-model.number="directSplit.cie"
                    :disabled="!isDirectSplitEditing" placeholder="%"></b-form-input>
                </td>
              </tr>
              <tr>
                <td>Semester End Examination</td>
                <td>
                  <b-form-input type="number" style="width: 100px;" v-model.number="directSplit.see"
                    :disabled="!isDirectSplitEditing" placeholder="%"></b-form-input>
                </td>
              </tr>
            </tbody>
          </table>
          <b-button variant="info" size="sm" class="mt-1" @click="setDirectSplit()" :disabled="!isDirectSplitEditing">Update</b-button>
        </div>

        <hr>

        <!-- Section 3: Final Split -->
        <div class="d-flex align-items-center">
          <b>Final Split</b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;"
            class="fas fa-edit" @click="toggleFinalSplitEdit"></i>
        </div>
        <div class="mt-2">
          <table class="table table-bordered table-sm" style="max-width: 400px;">
            <tbody>
              <tr>
                <td>Direct Weightage</td>
                <td>
                  <b-form-input type="number" style="width: 100px;" v-model.number="finalSplit.direct"
                    :disabled="!isFinalSplitEditing" placeholder="%"></b-form-input>
                </td>
              </tr>
              <tr>
                <td>Indirect Weightage</td>
                <td>
                  <b-form-input type="number" style="width: 100px;" v-model.number="finalSplit.indirect"
                    :disabled="!isFinalSplitEditing" placeholder="%"></b-form-input>
                </td>
              </tr>
            </tbody>
          </table>
          <b-button variant="info" size="sm" class="mt-1" @click="setFinalSplit()" :disabled="!isFinalSplitEditing">Update</b-button>
        </div>
      </div>

      <br><br>
      <div class="p-4 card">
        <div>
          <b><u>Course PO/PSO Attainment Calculation Methodology</u></b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;" class="fas fa-edit"
            @click="toggleAttainmentCalculationEdit"></i>
          <br />
        </div>

        <div>
          <b-form-group
            label="Select Calculation Method:"
            label-cols-sm="12"
            label-size="sm"
            class="mt-2"
          >
            <b-form-radio
              v-model="course_po_pso_attainment_calculating_method"
              :value="1"
              :disabled="!isAttainmentCalculationEditing"
            >
              Equation 1  <p>PO Attainment (PO1) = Σ(CO Attainment * CO-PO1 Mapping) / Σ(CO-PO1 Mapping)</p>
            </b-form-radio>

            <b-form-radio
              v-model="course_po_pso_attainment_calculating_method"
              :value="2"
              :disabled="!isAttainmentCalculationEditing"
            >
              Equation 2 <p>PO Attainment (PO1) = Σ(CO Attainment * CO-PO1 Mapping /3) / Number of CO </p>
            </b-form-radio>
          </b-form-group>

          <b-button variant="info" class="m-2" @click="setAttainmentCoursePoPsoAttainmentCalculatingMethod()"
          :disabled="!isAttainmentCalculationEditing">Update</b-button>
        </div>
      </div>

      <br><br>
      <div class="p-4 card">
        <div>
          <b><u>Result Analysis Calculation</u></b>
          <i v-if="!usersubgroup_id" style="cursor: pointer; margin-left: 10px; font-size: 16px !important; color: #1ea2ed;" class="fas fa-edit"
            @click="toggleResultAnalysisEdit"></i>
          <br />
        </div>

        <div class="mt-2">
          <b-form-group
            label="Select Calculation Method:"
            label-cols-sm="12"
            label-size="sm"
            class="mt-2"
          >
            <b-form-radio
              v-model="result_analysis_method"
              :value="1"
              :disabled="!isResultAnalysisEditing"
            >
            <p>Total Number of Student Passed/Total Number of Student Registered</p>
            </b-form-radio>

            <b-form-radio
              v-model="result_analysis_method"
              :value="2"
              :disabled="!isResultAnalysisEditing"
            >
            <p>Total Number of Student Passed/Total Number of Student Appeared</p>
            </b-form-radio>
          </b-form-group>

          <b-button variant="info" class="m-2" @click="setResultAnalysisMethod()"
          :disabled="!isResultAnalysisEditing">Update</b-button>
        </div>
      </div>


      <!-- Add Weightage Pair Modal -->
      <b-modal id="addWeightagePairModal" title="Add Weightage Pair" hide-footer centered>
        <b-form-group label="Select Weightage Types:">
          <b-form-checkbox
            v-for="type in weightageTypes"
            :key="type.key"
            v-model="newPair.types[type.key].selected"
            class="mb-1"
          >{{ type.label }}</b-form-checkbox>
        </b-form-group>

        <div v-if="hasAnyTypeSelected">
          <label><strong>Set Weightage (%) for selected types:</strong></label>
          <template v-for="type in weightageTypes">
            <b-row v-if="newPair.types[type.key].selected" :key="type.key" class="mb-2 align-items-center">
              <b-col cols="5">{{ type.label }}</b-col>
              <b-col cols="7">
                <b-form-input
                  type="number"
                  v-model.number="newPair.types[type.key].weightage"
                  placeholder="%"
                  style="width: 100px;"
                ></b-form-input>
              </b-col>
            </b-row>
          </template>
        </div>

        <div class="mt-3">
          <b-button variant="primary" size="sm" :disabled="!hasAnyTypeSelected" @click="addWeightagePair()">Add Pair</b-button>
          <b-button variant="danger" size="sm" class="ml-2" @click="$bvModal.hide('addWeightagePairModal')">Cancel</b-button>
        </div>
      </b-modal>
    </div>
</template>

<script>
export default {
  props: ["group_id", "usergroup_id", "usersubgroup_id"],
  async created() {
    await this.getAttainmentConfiguration();

    await this.getAttainmentCoursePoPsoAttainmentCalculatingMethod();

    await this.getResultAnalysisMethod();

    await this.getAttainmentThreshold();

    await this.getWeightagePairs();

    await this.getWeightageLocked();

    await this.getDirectSplit();

    await this.getFinalSplit();

  },
  data() {
    return {
      config: true,
      isEditing: false,
      isAttainmentCalculationEditing: false,
      isResultAnalysisEditing: false,
      isThresholdEditing: false,
      course_po_pso_attainment_calculating_method: 2,
      result_analysis_method: 1,
      selectedEquationType: {
        assessment: null,
        assignment: null,
        boardexam: null
      },
      attainmentThresholds: {
        equal_thresholds: true,
        locked: false,
        assessment: [
          { value: 1, comparisan: '>=', compare_by: 50 },
          { value: 2, comparisan: '>', compare_by: 65 },
          { value: 3, comparisan: '>', compare_by: 80 },
        ],
        assignment: [],
        laboratory: [],
        project: [],
        seminar: [],
        boardexam: [],
      },
      operators: [
        { value: null, text: "Select Operator" },
        { value: "<", text: "<" },
        { value: ">", text: ">" },
        { value: "<=", text: "<=" },
        { value: ">=", text: ">=" },
      ],
      weightagePairs: [],
      weightageLocked: false,
      isDirectSplitEditing: false,
      directSplit: { cie: null, see: null },
      isFinalSplitEditing: false,
      finalSplit: { direct: null, indirect: null },
      weightageTypes: [
        { key: 'assignment', label: 'Assignment' },
        { key: 'assessment', label: 'Assessment' },
        { key: 'laboratory', label: 'Laboratory' },
        { key: 'project', label: 'Project' },
        { key: 'seminar', label: 'Seminar' },
      ],
      newPair: {

        types: {
          assignment: { selected: false, weightage: null },
          assessment: { selected: false, weightage: null },
          laboratory: { selected: false, weightage: null },
          project: { selected: false, weightage: null },
          seminar: { selected: false, weightage: null },
        }
      },
    };
  },
  computed: {
    hasAnyTypeSelected() {
      return Object.values(this.newPair.types).some(t => t.selected);
    },
  },
  methods: {
    toggleEdit() {
      this.isEditing = !this.isEditing;
      if (!this.isEditing) {
        // Reset the selection if the user exits edit mode
        this.getAttainmentConfiguration();
      }
    },

    toggleAttainmentCalculationEdit() {
      this.isAttainmentCalculationEditing = !this.isAttainmentCalculationEditing;
      if (!this.isAttainmentCalculationEditing) {
        // Reset the selection if the user exits edit mode
        this.getAttainmentCoursePoPsoAttainmentCalculatingMethod();
      }
    },

    toggleResultAnalysisEdit() {
      this.isResultAnalysisEditing = !this.isResultAnalysisEditing;
      if (!this.isResultAnalysisEditing) {
        // Reset the selection if the user exits edit mode
        this.getResultAnalysisMethod();
      }
    },

    clearSelectedMethods() {
        this.selectedEquationType = { assessment: null, assignment: null, boardexam: null };
        this.setAttainmentConfiguration();
    },

    async setAttainmentCoursePoPsoAttainmentCalculatingMethod() {
        try {
          const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/course_po_pso_attainment_calculating_method`;
          await this.$axios.post(url, { course_po_pso_attainment_calculating_method: this.course_po_pso_attainment_calculating_method });
          this.$toast.success("Updated", { position: "top", duration: 3000 });
          this.isAttainmentCalculationEditing = false; // Disable edit mode after update
        } catch (error) {
          console.error("Error updating attainment configuration:", error);
          this.$toast.error("Update failed!", { position: "top", duration: 3000 });
        }
    },

    async setResultAnalysisMethod() {
        try {
          const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/result_analysis_method`;
          await this.$axios.post(url, { result_analysis_method: this.result_analysis_method });
          this.$toast.success("Updated", { position: "top", duration: 3000 });
          this.isResultAnalysisEditing = false; // Disable edit mode after update
        } catch (error) {
          console.error("Error updating result analysis method configuration:", error);
          this.$toast.error("Update failed!", { position: "top", duration: 3000 });
        }
    },

    async setAttainmentConfiguration() {
        try {
          const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/attainment_calculation_method`;
          await this.$axios.post(url, { attainment_calculation_method: this.selectedEquationType });
          this.$toast.success("Updated", { position: "top", duration: 3000 });
          this.isEditing = false; // Disable edit mode after update
        } catch (error) {
          console.error("Error updating attainment configuration:", error);
          this.$toast.error("Update failed!", { position: "top", duration: 3000 });
        }
    },

  async getResultAnalysisMethod() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/result_analysis_method`;
        const response = await this.$axios.get(url);
        const value = parseInt(response.data);
        this.result_analysis_method = isNaN(value) ? 1 : value;

      } catch (error) {
        console.error("Error fetching attainment configuration:", error);

      }
    },

  async getAttainmentCoursePoPsoAttainmentCalculatingMethod() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/course_po_pso_attainment_calculating_method`;
        const response = await this.$axios.get(url);
        const value = parseInt(response.data);
        this.course_po_pso_attainment_calculating_method = isNaN(value) ? 2 : value;

      } catch (error) {
        console.error("Error fetching attainment configuration:", error);

        this.selectedEquationType = {
          assessment: null,
          assignment: null,
          boardexam: null
        };
      }
    },
        
    toggleThresholdEdit() {
      this.isThresholdEditing = !this.isThresholdEditing;
      if (!this.isThresholdEditing) {
        this.getAttainmentThreshold();
      }
    },

    changeInAttainmentToolsEqualThresholds() {
      const defaultLevels = () => [
        { value: 1, comparisan: '>=', compare_by: 50 },
        { value: 2, comparisan: '>', compare_by: 65 },
        { value: 3, comparisan: '>', compare_by: 80 },
      ];
      const tools = ['assignment', 'laboratory', 'project', 'seminar', 'boardexam'];
      if (!this.attainmentThresholds.equal_thresholds) {
        tools.forEach(tool => { this.attainmentThresholds[tool] = defaultLevels(); });
      } else {
        tools.forEach(tool => { this.attainmentThresholds[tool] = []; });
      }
    },

    addThresholdValue(tool) {
      this.attainmentThresholds[tool].push({ value: "", comparisan: null, compare_by: "" });
      this.$forceUpdate();
    },

    async setAttainmentThreshold() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/attainment_threshold`;
        await this.$axios.post(url, { attainment_threshold: this.attainmentThresholds });
        this.$toast.success("Updated", { position: "top", duration: 3000 });
        this.isThresholdEditing = false;
      } catch (error) {
        console.error("Error updating attainment threshold:", error);
        this.$toast.error("Update failed!", { position: "top", duration: 3000 });
      }
    },

    async getAttainmentThreshold() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/attainment_threshold`;
        const response = await this.$axios.get(url);
        if (response.data && typeof response.data === 'object') {
          this.attainmentThresholds = response.data;
        }
      } catch (error) {
        console.error("Error fetching attainment threshold:", error);
      }
    },

    async getAttainmentConfiguration() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/attainment_calculation_method`;
        const response = await this.$axios.get(url);

        this.selectedEquationType = {
          assessment: response.data?.assessment ?? null,
          assignment: response.data?.assignment ?? null,
          boardexam: response.data?.boardexam ?? null
        };
      } catch (error) {
        console.error("Error fetching attainment configuration:", error);

        this.selectedEquationType = {
          assessment: null,
          assignment: null,
          boardexam: null
        };
      }
    },

    openAddPairModal() {
      this.newPair = {

        types: {
          assignment: { selected: false, weightage: null },
          assessment: { selected: false, weightage: null },
          laboratory: { selected: false, weightage: null },
          project: { selected: false, weightage: null },
          seminar: { selected: false, weightage: null },
        }
      };
      this.$bvModal.show('addWeightagePairModal');
    },

    async addWeightagePair() {
      if (!this.hasAnyTypeSelected) return;

      const selectedTypes = Object.values(this.newPair.types).filter(t => t.selected);

      const allFilled = selectedTypes.every(t => t.weightage !== null && t.weightage !== '' && Number(t.weightage) > 0);
      if (!allFilled) {
        this.$toast.error('All selected types must have a weightage value.', { position: "top", duration: 4000 });
        return;
      }

      const total = selectedTypes.reduce((sum, t) => sum + Number(t.weightage), 0);
      if (total !== 100) {
        this.$toast.error(`Weightage must sum to 100 (currently ${total})`, { position: "top", duration: 4000 });
        return;
      }

      this.$bvModal.hide('addWeightagePairModal');
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/${this.group_id}/weightage-cie-pairs`;
        await this.$axios.post(url, this.newPair);
        this.$toast.success("Pair added", { position: "top", duration: 3000 });
        await this.getWeightagePairs();
      } catch (error) {
        console.error("Error saving weightage pair:", error);
        this.$toast.error("Save failed!", { position: "top", duration: 3000 });
      }
    },

    async removeWeightagePair(index) {
      const pair = this.weightagePairs[index];
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/${this.group_id}/weightage-cie-pairs/${pair.id}`;
        await this.$axios.delete(url);
        await this.getWeightagePairs();
      } catch (error) {
        console.error("Error removing weightage pair:", error);
        this.$toast.error("Remove failed!", { position: "top", duration: 3000 });
      }
    },

    toggleFinalSplitEdit() {
      this.isFinalSplitEditing = !this.isFinalSplitEditing;
      if (!this.isFinalSplitEditing) {
        this.getFinalSplit();
      }
    },

    async setFinalSplit() {
      const total = Number(this.finalSplit.direct || 0) + Number(this.finalSplit.indirect || 0);
      if (total !== 100) {
        this.$toast.error(`Values must sum to 100 (currently ${total})`, { position: "top", duration: 4000 });
        return;
      }
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/final_split`;
        await this.$axios.post(url, { final_split: this.finalSplit });
        this.$toast.success("Updated", { position: "top", duration: 3000 });
        this.isFinalSplitEditing = false;
      } catch (error) {
        console.error("Error updating final split:", error);
        this.$toast.error("Update failed!", { position: "top", duration: 3000 });
      }
    },

    async getFinalSplit() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/final_split`;
        const response = await this.$axios.get(url);
        if (response.data && typeof response.data === 'object') {
          this.finalSplit = response.data;
        }
      } catch (error) {
        console.error("Error fetching final split:", error);
      }
    },

    toggleDirectSplitEdit() {
      this.isDirectSplitEditing = !this.isDirectSplitEditing;
      if (!this.isDirectSplitEditing) {
        this.getDirectSplit();
      }
    },

    async setDirectSplit() {
      const total = Number(this.directSplit.cie || 0) + Number(this.directSplit.see || 0);
      if (total !== 100) {
        this.$toast.error(`Values must sum to 100 (currently ${total})`, { position: "top", duration: 4000 });
        return;
      }
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/direct_attainment_split`;
        await this.$axios.post(url, { direct_attainment_split: this.directSplit });
        this.$toast.success("Updated", { position: "top", duration: 3000 });
        this.isDirectSplitEditing = false;
      } catch (error) {
        console.error("Error updating direct attainment split:", error);
        this.$toast.error("Update failed!", { position: "top", duration: 3000 });
      }
    },

    async getDirectSplit() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/direct_attainment_split`;
        const response = await this.$axios.get(url);
        if (response.data && typeof response.data === 'object') {
          this.directSplit = response.data;
        }
      } catch (error) {
        console.error("Error fetching direct attainment split:", error);
      }
    },

    async getWeightageLocked() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/weightage_locked`;
        const response = await this.$axios.get(url);
        this.weightageLocked = response.data === true || response.data === 'true' || response.data === 1;
      } catch (error) {
        console.error("Error fetching weightage lock:", error);
      }
    },

    async setWeightageLocked() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${this.group_id}/weightage_locked`;
        await this.$axios.post(url, { weightage_locked: this.weightageLocked });
        this.$toast.success(this.weightageLocked ? "Weightage locked" : "Weightage unlocked", { position: "top", duration: 2000 });
      } catch (error) {
        console.error("Error saving weightage lock:", error);
        this.$toast.error("Save failed!", { position: "top", duration: 3000 });
        this.weightageLocked = !this.weightageLocked;
      }
    },

    async getWeightagePairs() {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/${this.group_id}/weightage-cie-pairs`;
        const response = await this.$axios.get(url);
        if (Array.isArray(response.data)) {
          this.weightagePairs = response.data;
        }
      } catch (error) {
        console.error("Error fetching weightage pairs:", error);
      }
    },

  }
};
</script>

<style scoped>
.textBook .ltp_credit {
  width: 25%;
  display: flex;
  justify-content: space-between;
}

.textBook .ltp_credit input {
  width: 150px;
  text-align: center;
}
</style>
